/* BuyerCardFlowDiagram — buyer card-processing money path, styled to match the
 * diligence page (light slate cards + lime accent). Rendered directly under the
 * DDA FlowOfFundsDiagram on /diligence. Shows how a buyer card payment moves
 * into their U.S. Bank DDA.
 */
import { CreditCard, Lock, RefreshCw, Building2, Wallet, ArrowRight } from "lucide-react";
import { Fragment } from "react";

const STEPS = [
  { icon: CreditCard, t: "Buyer card",      s: "debit / credit at checkout" },
  { icon: Lock,       t: "PCI-tokenized vault", s: "card data never touches Verqo" },
  { icon: RefreshCw,  t: "Card network",    s: "Visa / Mastercard auth" },
  { icon: Building2,  t: "U.S. Bank",       s: "card acquiring + settlement" },
  { icon: Wallet,     t: "Buyer DDA",       s: "credited · ready to pay seller" },
];

const META = [
  ["Fee", "2.9% + 30¢ (buyer-side)"],
  ["Timing", "Instant auth · 2-3d settle"],
  ["Chargeback window", "Open 60 days"],
];

export default function BuyerCardFlowDiagram() {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6 md:p-8" data-testid="buyer-card-flow-diligence">
      <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-lime-600 font-semibold">
        Card processing · buyer
      </div>
      <h3 className="mt-1 text-lg md:text-xl font-semibold tracking-tight text-slate-900">
        How a buyer card payment moves into their U.S. Bank DDA
      </h3>
      <p className="mt-1.5 text-[13px] text-slate-500 max-w-2xl leading-relaxed">
        Card data is PCI-tokenized and never reaches Verqo. Funds settle through U.S. Bank into the buyer's dedicated DDA, then move DDA→DDA to pay the seller.
      </p>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-9 gap-2 items-stretch">
        {STEPS.map((st, i) => (
          <Fragment key={i}>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3.5 text-center flex flex-col items-center">
              <div className="w-9 h-9 rounded-lg bg-lime-100 text-lime-700 grid place-items-center mb-2">
                <st.icon className="w-4 h-4" strokeWidth={1.8} />
              </div>
              <div className="text-[12.5px] font-semibold text-slate-900 leading-tight">{st.t}</div>
              <div className="text-[10.5px] text-slate-500 mt-0.5 leading-tight">{st.s}</div>
            </div>
            {i < STEPS.length - 1 && (
              <div className="hidden lg:flex items-center justify-center">
                <ArrowRight className="w-4 h-4 text-slate-300" />
              </div>
            )}
          </Fragment>
        ))}
      </div>

      <div className="mt-5 grid sm:grid-cols-3 gap-2.5 text-[12px]">
        {META.map(([k, v]) => (
          <div key={k} className="rounded-lg border border-slate-200 p-3">
            <span className="block text-[9.5px] uppercase tracking-[0.18em] text-slate-400 mb-1">{k}</span>
            <span className="font-medium text-slate-800">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
